import { usePageRefresh, usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectDetails, getTasksList, transitionTask, changeTaskPriority, changeTaskTitle, deleteTask } from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import Pagination from '../../../components/common/Pagination';
import BadgeSelect from '../../../components/BadgeSelect';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { notify } from '../../../utils/notification';
import {
    statusBadgeClass, priorityBadgeClass, projectStatusBadgeClass, humanize,
    TASK_PRIORITIES, getAllowedTransitions, getTransitionPermission,
} from '../../../consts/taskConstants';

const fmtDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const initials = (first, last) => `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';
const PRIORITY_OPTIONS = TASK_PRIORITIES.map((p) => ({ value: p, label: humanize(p), badgeClass: priorityBadgeClass(p) }));
const fullName = (p) => `${p?.first_name || ''} ${p?.last_name || ''}`.trim();
// Primary assignee + any active additional assignees, de-duped by user.
const assigneesOf = (task) => {
    const out = [];
    const seen = new Set();
    const push = (p, primary) => {
        if (!p) return;
        const uid = p.user_id;
        if (uid != null) { if (seen.has(uid)) return; seen.add(uid); }
        out.push({ first_name: p.first_name, last_name: p.last_name, role: p.role, user_id: uid, primary });
    };
    push(task.assignee, true);
    (Array.isArray(task.assignments) ? task.assignments : [])
        .filter((a) => a.status === 'ACTIVE' || a.status == null)
        .forEach((a) => push({ first_name: a.user?.first_name, last_name: a.user?.last_name, user_id: a.user_id ?? a.user?.user_id, role: a.role }, false));
    return out;
};
const isOverdue = (task) =>
    task.due_date && !['COMPLETED', 'CLOSED', 'CANCELLED'].includes(task.status) &&
    new Date(task.due_date).getTime() < Date.now();

const ProjectTasks = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const {
        projectDetailsData: project, projectDetailsLoading,
        tasksListData, tasksListLoading, tasksPagination, deleteTaskLoading,
    } = useSelector((s) => s.TaskReducer);

    const { loginData } = useSelector((s) => s.loginReducer);
    const currentUserId = loginData?.user?.user_id ?? loginData?.user?.id;
    const isMe = (p) => currentUserId != null && p?.user_id != null && String(p.user_id) === String(currentUserId);

    const [toDelete, setToDelete] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);

    const fetchTasks = useCallback(() => {
        dispatch(getTasksList({ project_id: projectId, page: currentPage, limit: itemsPerPage, sort_by: 'created_at', sort_dir: 'desc' }));
    }, [dispatch, projectId, currentPage, itemsPerPage]);
    usePageRefresh(fetchTasks);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getProjectDetails({ projectId }));
    }, [dispatch, projectId, refreshKey]);

    useEffect(() => { fetchTasks(); }, [fetchTasks]);

    // Reset to first page when the project changes.
    useEffect(() => { setCurrentPage(1); }, [projectId]);

    const handleStatusChange = (task, nextStatus) => {
        if (!nextStatus || nextStatus === task.status) return;
        if (!getAllowedTransitions(task.status).includes(nextStatus)) {
            notify('warning', `Can't move ${humanize(task.status)} → ${humanize(nextStatus)}`);
            return;
        }
        if (!can(getTransitionPermission(nextStatus))) {
            notify('warning', 'You do not have permission for this status');
            return;
        }
        dispatch(transitionTask({ taskId: task.task_id, status: nextStatus, onSuccess: fetchTasks }));
    };

    const handlePriorityChange = (task, nextPriority) => {
        if (!nextPriority || nextPriority === task.priority) return;
        if (!can(PERMISSIONS.TASK_UPDATE)) {
            notify('warning', 'You do not have permission to change priority');
            return;
        }
        dispatch(changeTaskPriority({ taskId: task.task_id, priority: nextPriority, onSuccess: fetchTasks }));
    };

    // Inline title editing
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const cancelNextBlur = useRef(false);
    const startEditTitle = (task) => { setEditingId(task.task_id); setEditTitle(task.title || ''); };
    const cancelEditTitle = () => { setEditingId(null); setEditTitle(''); };
    const saveTitle = (task) => {
        if (cancelNextBlur.current) { cancelNextBlur.current = false; cancelEditTitle(); return; }
        const t = editTitle.trim();
        if (!t || t === task.title) { cancelEditTitle(); return; }
        dispatch(changeTaskTitle({ taskId: task.task_id, title: t, onSuccess: () => { cancelEditTitle(); fetchTasks(); } }));
    };

    const confirmDelete = () => {
        if (!toDelete) return;
        dispatch(deleteTask({
            taskId: toDelete.task_id,
            onSuccess: () => { setToDelete(null); fetchTasks(); dispatch(getProjectDetails({ projectId })); },
        }));
    };

    const tasks = useMemo(() => (Array.isArray(tasksListData) ? tasksListData : []), [tasksListData]);

    const totalTasks = tasksPagination?.total ?? tasks.length;
    const stats = useMemo(() => {
        const done = tasks.filter((t) => ['COMPLETED', 'CLOSED'].includes(t.status)).length;
        const inProgress = tasks.filter((t) => t.status === 'IN_PROGRESS').length;
        const overdue = tasks.filter(isOverdue).length;
        const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
        return { total: totalTasks, done, inProgress, overdue, pct };
    }, [tasks, totalTasks]);

    const loading = projectDetailsLoading && !project;

    if (loading) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading project...</p>
            </div>
        );
    }
    if (!project || String(project.project_id) !== String(projectId)) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                <Icon icon="hugeicons:folder-01" className="tw-text-4xl tw-text-gray-300" />
                <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Project not found</p>
                <button onClick={() => navigate('/society-portal/tasks/projects')} className="tw-mt-3 tw-text-blue-600 tw-text-sm">Back to projects</button>
            </div>
        );
    }

    const emp = project.employee;

    return (
        <>
            {/* Breadcrumb */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-min-w-0">
                    <button onClick={() => navigate('/society-portal/tasks/projects')} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:arrow-left" />
                    </button>
                    <button onClick={() => navigate('/society-portal/tasks/projects')} className="hover:tw-text-gray-700">Projects</button>
                    <Icon icon="mdi:chevron-right" className="tw-text-gray-300" />
                    <span className="tw-text-gray-700 tw-font-medium tw-truncate tw-max-w-[220px]">{project.name}</span>
                </div>
                {can(PERMISSIONS.TASK_CREATE) && (
                    <button onClick={() => navigate('/society-portal/tasks/add')} className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-flex-shrink-0">
                        <Icon icon="mdi:plus" /> New Task
                    </button>
                )}
            </div>

            {/* Project header */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden tw-mb-4">
                <div className="tw-h-1 tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-500" />
                <div className="tw-p-5 tw-flex tw-flex-col lg:tw-flex-row tw-gap-5">
                    {/* Info */}
                    <div className="tw-flex tw-items-start tw-gap-3.5 tw-flex-1 tw-min-w-0">
                        <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm">
                            <Icon icon="hugeicons:folder-01" className="tw-text-white tw-text-2xl" />
                        </div>
                        <div className="tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                <h1 className="tw-m-0 tw-text-lg tw-font-bold tw-text-gray-800 tw-break-words">{project.name}</h1>
                                <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${projectStatusBadgeClass(project.status)}`}>{humanize(project.status)}</span>
                            </div>
                            <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-gray-500 tw-leading-relaxed tw-line-clamp-2 tw-max-w-3xl">
                                {project.description || 'No description provided.'}
                            </p>
                            <div className="tw-flex tw-items-center tw-gap-x-5 tw-gap-y-2 tw-mt-3.5 tw-flex-wrap">
                                <div>
                                    <p className="tw-m-0 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wide">Timeline</p>
                                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-medium tw-text-gray-700 tw-mt-0.5">
                                        <Icon icon="mdi:calendar-blank-outline" className="tw-text-gray-400" />
                                        <span>{fmtDate(project.start_date)}</span>
                                        <Icon icon="mdi:arrow-right-thin" className="tw-text-gray-300" />
                                        <span>{fmtDate(project.due_date)}</span>
                                    </div>
                                </div>
                                <div className="tw-h-8 tw-w-px tw-bg-gray-100 tw-hidden sm:tw-block" />
                                <div>
                                    <p className="tw-m-0 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wide">Owner</p>
                                    {emp ? (
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                                            <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-text-[10px] tw-font-bold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{initials(emp.first_name, emp.last_name)}</span>
                                            <div className="tw-leading-tight">
                                                <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-gray-700">{`${emp.first_name || ''} ${emp.last_name || ''}`.trim()}</p>
                                                {emp.designation && <p className="tw-m-0 tw-text-[11px] tw-text-gray-400">{emp.designation}</p>}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-text-gray-400 tw-mt-1">
                                            <Icon icon="mdi:account-off-outline" /> Unassigned
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Progress ring */}
                    <div className="tw-flex tw-items-center tw-gap-4 tw-flex-shrink-0 lg:tw-border-l lg:tw-border-gray-100 lg:tw-pl-5">
                        <div className="tw-relative tw-w-[68px] tw-h-[68px]">
                            <svg className="tw-w-full tw-h-full tw--rotate-90" viewBox="0 0 68 68">
                                <circle cx="34" cy="34" r="30" fill="none" stroke="#eef2f7" strokeWidth="7" />
                                <circle cx="34" cy="34" r="30" fill="none" stroke="#4f46e5" strokeWidth="7" strokeLinecap="round"
                                    strokeDasharray={2 * Math.PI * 30}
                                    strokeDashoffset={(2 * Math.PI * 30) * (1 - stats.pct / 100)}
                                    className="tw-transition-all" />
                            </svg>
                            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                                <span className="tw-text-sm tw-font-bold tw-text-gray-800">{stats.pct}%</span>
                            </div>
                        </div>
                        <div>
                            <p className="tw-m-0 tw-text-lg tw-font-bold tw-text-gray-800">{stats.done}<span className="tw-text-gray-400 tw-font-medium">/{stats.total}</span></p>
                            <p className="tw-m-0 tw-text-[11px] tw-text-gray-500">Tasks completed</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stat chips */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                {[
                    { icon: 'hugeicons:task-01', bg: 'tw-bg-blue-50', color: 'tw-text-blue-600', value: stats.total, label: 'Total Tasks' },
                    { icon: 'mdi:progress-clock', bg: 'tw-bg-sky-50', color: 'tw-text-sky-600', value: stats.inProgress, label: 'In Progress' },
                    { icon: 'mdi:alert-circle-outline', bg: 'tw-bg-red-50', color: 'tw-text-red-600', value: stats.overdue, label: 'Overdue' },
                    { icon: 'mdi:check-circle-outline', bg: 'tw-bg-emerald-50', color: 'tw-text-emerald-600', value: stats.done, label: 'Completed' },
                ].map((s, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-xl tw-p-3.5 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-3">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${s.bg} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <Icon icon={s.icon} className={`${s.color} tw-text-lg`} />
                        </div>
                        <div>
                            <p className="tw-m-0 tw-text-xl tw-font-bold tw-text-gray-800 tw-leading-none">{s.value}</p>
                            <p className="tw-m-0 tw-mt-1 tw-text-[11px] tw-text-gray-500">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tasks section */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-700">Tasks <span className="tw-text-gray-400 tw-font-normal">({totalTasks})</span></p>
                </div>

                {tasksListLoading ? (
                    <div className="tw-text-center tw-py-12">
                        <Icon icon="mdi:loading" className="tw-text-3xl tw-text-blue-500 tw-animate-spin" />
                        <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading tasks...</p>
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="tw-text-center tw-py-12">
                        <Icon icon="hugeicons:task-01" className="tw-text-4xl tw-text-gray-300" />
                        <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No tasks in this project yet</p>
                        {can(PERMISSIONS.TASK_CREATE) && (
                            <button onClick={() => navigate('/society-portal/tasks/add')} className="tw-mt-3 tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm hover:tw-bg-blue-700">
                                <Icon icon="mdi:plus" /> Add first task
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-sm">
                            <thead>
                                <tr className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-5 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Task</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Priority</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Status</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Due Date</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Assigned To</th>
                                    <th className="tw-px-3 tw-py-2.5 tw-w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.map((task) => (
                                    <tr key={task.task_id}
                                        onClick={() => editingId !== task.task_id && navigate(`/society-portal/tasks/view/${task.task_id}`)}
                                        className="tw-group tw-border-b tw-border-gray-50 last:tw-border-0 hover:tw-bg-blue-50/30 tw-transition-colors tw-cursor-pointer">
                                        <td className="tw-px-5 tw-py-1.5">
                                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                                <span className={`tw-w-2 tw-h-2 tw-rounded-full tw-flex-shrink-0 ${['COMPLETED', 'CLOSED'].includes(task.status) ? 'tw-bg-emerald-500' : task.status === 'IN_PROGRESS' ? 'tw-bg-sky-500' : 'tw-bg-gray-300'}`} />
                                                {editingId === task.task_id ? (
                                                    <input
                                                        autoFocus
                                                        value={editTitle}
                                                        maxLength={200}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => setEditTitle(e.target.value)}
                                                        onBlur={() => saveTitle(task)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                                                            if (e.key === 'Escape') { cancelNextBlur.current = true; e.currentTarget.blur(); }
                                                        }}
                                                        className="tw-flex-1 tw-min-w-0 tw-px-2 tw-py-1 tw-border tw-border-blue-400 tw-rounded tw-text-[13px] focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-400"
                                                    />
                                                ) : (
                                                    <div className="tw-min-w-0 tw-leading-tight tw-flex tw-items-center tw-gap-1.5">
                                                        <div className="tw-min-w-0">
                                                            <p className={`tw-m-0 tw-text-[13px] tw-font-medium tw-line-clamp-1 ${['COMPLETED', 'CLOSED'].includes(task.status) ? 'tw-text-gray-400 tw-line-through' : 'tw-text-gray-800'}`}>{task.title}</p>
                                                            <p className="tw-m-0 tw-text-[10px] tw-text-gray-400">{task.task_number}</p>
                                                        </div>
                                                        {can(PERMISSIONS.TASK_UPDATE) && (
                                                            <button type="button" title="Edit title"
                                                                onClick={(e) => { e.stopPropagation(); startEditTitle(task); }}
                                                                className="tw-opacity-0 group-hover:tw-opacity-100 tw-text-gray-300 hover:tw-text-blue-600 tw-transition tw-flex-shrink-0">
                                                                <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="tw-px-3 tw-py-1.5" onClick={(e) => e.stopPropagation()}>
                                            <BadgeSelect
                                                value={task.priority}
                                                label={humanize(task.priority)}
                                                badgeClass={priorityBadgeClass(task.priority)}
                                                disabled={!can(PERMISSIONS.TASK_UPDATE)}
                                                options={PRIORITY_OPTIONS}
                                                onChange={(v) => handlePriorityChange(task, v)}
                                            />
                                        </td>
                                        <td className="tw-px-3 tw-py-1.5" onClick={(e) => e.stopPropagation()}>
                                            <BadgeSelect
                                                value={task.status}
                                                label={humanize(task.status)}
                                                badgeClass={statusBadgeClass(task.status)}
                                                disabled={getAllowedTransitions(task.status).length === 0}
                                                options={[
                                                    { value: task.status, label: humanize(task.status), badgeClass: statusBadgeClass(task.status) },
                                                    ...getAllowedTransitions(task.status).map((s) => ({ value: s, label: humanize(s), badgeClass: statusBadgeClass(s), disabled: !can(getTransitionPermission(s)) })),
                                                ]}
                                                onChange={(v) => handleStatusChange(task, v)}
                                            />
                                        </td>
                                        <td className="tw-px-3 tw-py-1.5">
                                            <span className={`tw-text-[12px] tw-whitespace-nowrap ${isOverdue(task) ? 'tw-text-red-600 tw-font-medium' : 'tw-text-gray-600'}`}>{fmtDate(task.due_date)}</span>
                                        </td>
                                        <td className="tw-px-3 tw-py-1.5">
                                            {(() => {
                                                const people = assigneesOf(task);
                                                if (people.length === 0) return (
                                                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-gray-400">
                                                        <span className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                            <Icon icon="mdi:account-outline" className="tw-text-xs" />
                                                        </span>
                                                        <span className="tw-text-[12px]">Unassigned</span>
                                                    </div>
                                                );
                                                return (
                                                    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                                        <div className="tw-flex tw-items-center">
                                                            {people.slice(0, 3).map((p, idx) => (
                                                                <span key={idx} title={`${isMe(p) ? 'me: ' : ''}${fullName(p) || '—'}${p.primary ? ' (Primary)' : p.role ? ` (${humanize(p.role)})` : ''}`}
                                                                    className={`tw-w-6 tw-h-6 tw-rounded-full tw-text-[9px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-ring-2 tw-ring-white tw--ml-1.5 first:tw-ml-0 tw-flex-shrink-0 ${isMe(p) ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-blue-100 tw-text-blue-700'}`}>
                                                                    {initials(p.first_name, p.last_name)}
                                                                </span>
                                                            ))}
                                                            {people.length > 3 && (
                                                                <span className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-gray-100 tw-text-gray-500 tw-text-[9px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-ring-2 tw-ring-white tw--ml-1.5 tw-flex-shrink-0">
                                                                    +{people.length - 3}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {people.length === 1 && (
                                                            <span className="tw-text-[12px] tw-text-gray-700 tw-truncate">
                                                                {isMe(people[0]) && <span className="tw-font-semibold tw-text-emerald-600">me: </span>}
                                                                {fullName(people[0]) || '—'}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="tw-px-3 tw-py-1.5" onClick={(e) => e.stopPropagation()}>
                                            <div className="tw-flex tw-justify-center">
                                                {can(PERMISSIONS.TASK_DELETE) && (
                                                    <button type="button" title="Delete task" onClick={() => setToDelete(task)}
                                                        className="tw-opacity-0 group-hover:tw-opacity-100 tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-text-gray-400 hover:tw-bg-red-50 hover:tw-text-red-500 tw-transition">
                                                        <Icon icon="mdi:delete-outline" className="tw-text-sm" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {!tasksListLoading && tasks.length > 0 && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={tasksPagination?.totalPages || 1}
                        totalItems={totalTasks}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}
                        itemsPerPageOptions={[20, 50, 100]}
                        itemLabel="tasks"
                    />
                </div>
            )}

            {/* Delete confirmation */}
            {toDelete && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-gray-800">Delete Task</h3>
                                <p className="tw-m-0 tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">Delete <span className="tw-font-semibold">{toDelete.title}</span>?</p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button onClick={() => setToDelete(null)} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">Cancel</button>
                            <button onClick={confirmDelete} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50">
                                {deleteTaskLoading ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Deleting...</> : <><Icon icon="mdi:delete" /> Delete</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProjectTasks;
